import React from 'react';
import { useSelector } from 'react-redux';
import SideNavbar from '../../components/Navbar/SideNavbar';
import Header from '../../components/Header/Header';
import { FiUser, FiMail, FiCalendar, FiShield, FiClock } from 'react-icons/fi';
import { MdVerifiedUser } from 'react-icons/md';
import { BiTime } from 'react-icons/bi';

// @@ AI-COMMENT: Panel header bar shared by every section, matching the CMS listing chrome
const PanelHead = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <div className="px-4 py-2.5 border-b border-gray-200 flex items-center gap-2">
        <span className="text-blue-500 flex items-center">{icon}</span>
        <span className="text-xs font-semibold text-[#43425D]">{title}</span>
    </div>
);

// @@ AI-COMMENT: Label/value pair styled like the CMS filter fields
const DetailItem = ({ icon, label, value, valueClass = 'text-[#3F3F3F]' }: {
    icon: React.ReactNode;
    label: string;
    value: string;
    valueClass?: string;
}) => (
    <div className="flex items-start gap-2.5">
        <span className="w-7 h-7 rounded-full bg-[#E5EEFC] text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
            {icon}
        </span>
        <div className="min-w-0">
            <p className="text-[11px] text-gray-600 mb-0.5">{label}</p>
            <p className={`text-xs font-semibold break-words ${valueClass}`} title={value}>{value}</p>
        </div>
    </div>
);

const Profile = () => {
    // Pull user information from Redux auth store
    const store: any = useSelector((s) => s);
    const { user } = store.authStore;

    // The store starts as `{}` — truthy, so a whole-object fallback never fires
    // — and the user is a decoded JWT whose claims vary. Every field is
    // defaulted individually instead.
    const userProfile = {
        id: user?.id ?? null,
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        status: user?.status ?? false,
        createdAt: user?.createdAt || '',
        updatedAt: user?.updatedAt || '',
        roles: Array.isArray(user?.roles) ? user.roles : []
    };

    // Falls back to the email local part so the page still identifies the user
    // when the token carries no name.
    const fullName =
        `${userProfile.firstName} ${userProfile.lastName}`.trim() ||
        userProfile.email.split('@')[0] ||
        'User';

    const getInitials = () => {
        const first = userProfile.firstName.trim();
        const last = userProfile.lastName.trim();
        if (first || last) {
            return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
        }
        return userProfile.email.trim().charAt(0).toUpperCase() || '?';
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    {/* @@ AI-COMMENT: Page header matching the shared CMS listing header */}
                    <div className="p-4">
                        <p className="text-xl font-bold text-[#43425D]">PROFILE</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">Your account details and assigned access.</p>
                    </div>

                    {/* @@ AI-COMMENT: Identity strip */}
                    <div className="mt-2.5 bg-white px-4 py-4 flex flex-wrap items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-[#43425D] flex items-center justify-center flex-shrink-0">
                            <span className="text-lg font-bold text-white">{getInitials()}</span>
                        </div>
                        <div className="min-w-0">
                            <p className="text-base font-bold text-[#43425D] truncate">{fullName}</p>
                            <p className="text-xs text-gray-500 truncate">{userProfile.email || '—'}</p>
                        </div>
                        <span
                            className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${userProfile.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                        >
                            <MdVerifiedUser size={12} />
                            {userProfile.status ? 'Active' : 'Inactive'}
                        </span>
                    </div>

                    {/* @@ AI-COMMENT: Details and timeline sit side by side on wide screens, stacked below lg */}
                    <div className="mt-2.5 grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                        <div className="bg-white">
                            <PanelHead icon={<FiUser size={14} />} title="Basic Information" />
                            <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                <DetailItem
                                    icon={<FiUser size={13} />}
                                    label="Full Name"
                                    value={fullName}
                                />
                                <DetailItem
                                    icon={<FiMail size={13} />}
                                    label="Email Address"
                                    value={userProfile.email || '—'}
                                />
                                <DetailItem
                                    icon={<MdVerifiedUser size={13} />}
                                    label="Account Status"
                                    value={userProfile.status ? 'Active Account' : 'Inactive Account'}
                                    valueClass={userProfile.status ? 'text-green-600' : 'text-red-600'}
                                />
                            </div>
                        </div>

                        <div className="bg-white">
                            <PanelHead icon={<FiClock size={14} />} title="Account Timeline" />
                            <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                <DetailItem
                                    icon={<FiCalendar size={13} />}
                                    label="Account Created"
                                    value={formatDate(userProfile.createdAt)}
                                />
                                <DetailItem
                                    icon={<BiTime size={13} />}
                                    label="Last Updated"
                                    value={formatDate(userProfile.updatedAt)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* @@ AI-COMMENT: Assigned modules listed in the shared CMS table chrome */}
                    <div className="mt-2.5 bg-white mb-5">
                        <PanelHead icon={<FiShield size={14} />} title={`Access Permissions (${userProfile.roles.length})`} />
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[420px]">
                                <thead className="bg-[#343434]">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[70%]">Module</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[30%]">Access</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userProfile.roles.length === 0 ? (
                                        <tr>
                                            <td colSpan={2} className="px-4 py-8 text-center text-xs text-gray-500">
                                                No modules are assigned to your account.
                                            </td>
                                        </tr>
                                    ) : (
                                        userProfile.roles.map((role: any, index: number) => (
                                            <tr key={`${role}-${index}`} className="[&:nth-child(odd)]:bg-[#f7f7f7] hover:bg-[#eef4fb]">
                                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">
                                                    <span className="flex items-center gap-2">
                                                        <span className="w-6 h-6 rounded-full bg-[#E5EEFC] text-blue-600 flex items-center justify-center flex-shrink-0">
                                                            <FiShield size={12} />
                                                        </span>
                                                        <span className="font-semibold">{role}</span>
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-left text-xs">
                                                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold">
                                                        Full Access
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
